//@ts-nocheck
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { sentenceCase } from 'change-case';
import { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';

// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Avatar,
  Button,
  Popover,
  Checkbox,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Snackbar,
  Alert,
} from '@mui/material';
// components
import Label from '../components/label';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
// sections
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';
import EditUserQuota from '../components/EditUserQuota';
import useUpdateQuota from '../hooks/KnowledgeAI/useUpdateQuota';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'email', label: 'Email', alignRight: false },
  { id: 'role', label: 'Role', alignRight: false },
  { id: 'tokenQuota', label: 'Token Quota (Monthly)', alignRight: false },
  { id: 'tokensUsed', label: 'Tokens Used', alignRight: false },
  { id: 'remainingQuota', label: 'Remaining Quota', alignRight: false },
  { id: '' },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  if (array == null) return [];
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_user) => _user.name.toLowerCase().indexOf(query.toLowerCase()) !== -1);
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function UserPage() {
  const theme = useTheme();
  const [open, setOpen] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState(0);

  const [order, setOrder] = useState('asc');

  const [selected, setSelected] = useState([]);

  const [orderBy, setOrderBy] = useState('name');

  const [filterName, setFilterName] = useState('');

  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [userInfo, setUserInfo] = useState(null);
  const [selectedUserForMenu, setSelectedUserForMenu] = useState(null);
  const [editQuotaModalOpen, setEditQuotaModalOpen] = useState(false);
  const [quotaSuccessOpen, setQuotaSuccessOpen] = useState(false);
  const { updateQuota, loading: updateQuotaLoading } = useUpdateQuota();

  useEffect(() => {
    const load_user_info = async () => {
      //@ts-ignore
      fetch(process.env.REACT_APP_LIST_USERS_URL || '', {
        method: 'GET',
      })
        .then((response) => {
          return response.json();
        })
        .then((data) => {
          // Use the data from the response

          setUserInfo(data);
          userlist = Object.keys(data);
          // console.log('KEYS in project index mapping : ', userlist)
          // console.log('VALUES in project index mapping : ', Object.values(data))
        })
        .catch((err) => {
          console.log(err.message);
        });
    };
    // }
    if (userInfo == null) {
      load_user_info();
    }
  }, [isLoading, userInfo]);

  const handleOpenMenu = (event, user) => {
    setOpen(event.currentTarget);
    setSelectedUser(user);
    setSelectedUserForMenu(
      user ? { ...user, tokenQuota: user.tokens_allocated != null ? String(user.tokens_allocated) : '' } : null
    );
  };

  const handleCloseMenu = () => {
    setOpen(null);
    setSelectedUser(null);
    setSelectedUserForMenu(null);
  };

  const handleOpenEditQuota = () => {
    setOpen(null); // close popover only; keep selectedUserForMenu for the modal
    setEditQuotaModalOpen(true);
  };

  const handleCloseEditQuota = () => {
    setEditQuotaModalOpen(false);
    setSelectedUserForMenu(null);
  };

  const handleEditQuotaSaveSuccess = () => {
    handleCloseEditQuota();
    setUserInfo(null);
    setQuotaSuccessOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedUser || !selectedUser.email) {
      console.error('No user selected for deletion');
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete user "${selectedUser.name}" (${selectedUser.email})?\n\nThis will remove the user from both authentication and project mapping tables.`
    );

    if (!confirmDelete) {
      handleCloseMenu();
      return;
    }

    try {
      setIsLoading(true);
      const deleteUrl = process.env.REACT_APP_DELETE_USER_URL || '';

      const response = await fetch(deleteUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: selectedUser.email }),
      });

      if (response.ok) {
        alert(`User "${selectedUser.name}" deleted successfully`);
        // Refresh user list
        setUserInfo(null);
        setIsLoading(false);
      } else {
        const errorText = await response.text();
        alert(`Failed to delete user: ${errorText}`);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(`Error deleting user: ${error.message}`);
      setIsLoading(false);
    }

    handleCloseMenu();
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = UserInfo?.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - userInfo?.length) : 0;

  const filteredUsers = applySortFilter(userInfo, getComparator(order, orderBy), filterName);

  const isNotFound = !filteredUsers.length && !!filterName;

  return (
    <>
      <Helmet>
        <title> Knowledge AI </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mt={3}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              color: theme.palette.mode === 'dark' ? '#C7C9C7' : '#333f48',
            }}
          >
            Users
          </Typography>
          <Link to="/dashboard/create-user">
            <Button variant="contained" disabled className="primary-btn" startIcon={<Iconify icon="eva:plus-fill" />}>
              New User
            </Button>
          </Link>
        </Stack>

        <Card>
          <UserListToolbar numSelected={selected.length} filterName={filterName} onFilterName={handleFilterByName} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={userInfo?.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const { ID, name, email, role } = row;
                    const tokensAllocated = row.tokens_allocated != null ? String(row.tokens_allocated) : '—';
                    const tokensUsed = row.tokens_used != null ? row.tokens_used : '—';
                    const remainingQuotaValue =
                      row.tokens_allocated != null && row.tokens_used != null
                        ? Math.max(
                            0,
                            Math.round(((row.tokens_allocated - row.tokens_used) / row.tokens_allocated) * 100)
                          )
                        : null;
                    const remainingQuotaColor =
                      remainingQuotaValue === null
                        ? 'text.secondary'
                        : remainingQuotaValue === 0
                        ? 'error.main'
                        : remainingQuotaValue <= 30
                        ? 'warning.main'
                        : 'success.main';
                    const selectedUser = selected.indexOf(name) !== -1;

                    return (
                      <TableRow hover key={ID} tabIndex={-1} mgrname="checkbox" selected={selectedUser} disabled>
                        <TableCell padding="checkbox">
                          <Checkbox checked={selectedUser} onChange={(event) => handleClick(event, name)} disabled />
                        </TableCell>

                        <TableCell component="th" scope="row" padding="none">
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography variant="subtitle2" noWrap>
                              {name}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell align="left" className="font-medium">
                          {email}
                        </TableCell>
                        <TableCell align="left" className="font-medium">
                          {role}
                        </TableCell>

                        <TableCell align="left" className="font-medium">
                          {tokensAllocated}
                        </TableCell>

                        <TableCell align="left" className="font-medium">
                          {tokensUsed}
                        </TableCell>

                        <TableCell align="left">
                          <Typography variant="body2" fontWeight="bold" color={remainingQuotaColor}>
                            {remainingQuotaValue !== null ? `${remainingQuotaValue}%` : '—'}
                          </Typography>
                        </TableCell>

                        <TableCell align="right">
                          <IconButton
                            size="large"
                            color="inherit"
                            onClick={(e) =>
                              handleOpenMenu(e, {
                                ...row,
                                tokenQuota: row.tokens_allocated != null ? String(row.tokens_allocated) : '',
                              })
                            }
                          >
                            <Iconify icon={'eva:more-vertical-fill'} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <Paper
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="h6" paragraph>
                            Not found
                          </Typography>

                          <Typography variant="body2">
                            No results found for &nbsp;
                            <strong>&quot;{filterName}&quot;</strong>.
                            <br /> Try checking for typos or using complete words.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={userInfo?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem disabled>
          <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
          Edit
        </MenuItem>

        <MenuItem onClick={handleOpenEditQuota} disabled={!selectedUserForMenu}>
          <Iconify icon={'eva:pie-chart-outline'} sx={{ mr: 2 }} />
          Edit Quota
        </MenuItem>

        <MenuItem sx={{ color: 'error.main' }} onClick={handleDelete}>
          <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
          Delete
        </MenuItem>
      </Popover>

      <EditUserQuota
        open={editQuotaModalOpen}
        onClose={handleCloseEditQuota}
        user={selectedUserForMenu}
        onSaveSuccess={handleEditQuotaSaveSuccess}
        updateQuota={updateQuota}
        loading={updateQuotaLoading}
      />

      <Snackbar
        open={quotaSuccessOpen}
        autoHideDuration={4000}
        onClose={() => setQuotaSuccessOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setQuotaSuccessOpen(false)} severity="success">
          Token quota updated successfully.
        </Alert>
      </Snackbar>
    </>
  );
}
